// Pricing/spec table — keep in sync with live Shopify pricing and with the
// "Aantal modules" variant option values on the XDS 4.0 / XDS 4.1 products.
// Mirrors the tables in sections/xds-module-info.liquid and
// sections/xds-module-info-4-1.liquid (presale price column).
const KEUZEHULP_SPECS = {
  xds40: {
    2: { capacity: '4.8 kWh', price: '€1.899' },
    3: { capacity: '7.2 kWh', price: '€2.499' },
    4: { capacity: '9.6 kWh', price: '€3.049' },
    5: { capacity: '12 kWh', price: '€3.549' },
  },
  xds41: {
    2: { capacity: '5.76 kWh', price: '€2.399' },
    3: { capacity: '8.64 kWh', price: '€3.199' },
    4: { capacity: '11.52 kWh', price: '€3.949' },
    5: { capacity: '14.4 kWh', price: '€4.649' },
  },
};

// kWp per paneel × aannametactor voor benodigde opslag (vraag 3 van de keuzehulp).
const PANEL_KWP = 0.4;
const PANEL_TO_STORAGE_FACTOR = 1.5;

// Module-capaciteit en staffels per product. Elke staffel geeft het aantal
// modules dat nodig is om de "benodigde capaciteit" te dekken; overschrijdt
// die het maximum van de staffel, dan is er geen passend modulesadvies
// (zie updatePreview: XDS 4.0 verwijst dan door naar 4.1, XDS 4.1 verwijst
// door naar maatwerk/contact).
const XDS_MAPPING = {
  '4.0': {
    specKey: 'xds40',
    moduleCapacity: 2.4,
    thresholds: [
      { maxCapacity: 4.8, modules: 2 },
      { maxCapacity: 7.2, modules: 3 },
      { maxCapacity: 9.6, modules: 4 },
      { maxCapacity: 12, modules: 5 },
    ],
  },
  '4.1': {
    specKey: 'xds41',
    moduleCapacity: 2.88,
    thresholds: [
      { maxCapacity: 5.76, modules: 2 },
      { maxCapacity: 8.64, modules: 3 },
      { maxCapacity: 11.52, modules: 4 },
      { maxCapacity: 14.4, modules: 5 },
    ],
  },
};

const CAPACITY_EPSILON = 1e-9;

function getModulesForCapacity(product, capacity) {
  const cfg = XDS_MAPPING[product];
  if (!cfg) return null;
  const match = cfg.thresholds.find((t) => capacity <= t.maxCapacity + CAPACITY_EPSILON);
  return match ? match.modules : null;
}

function formatKwh(value) {
  const rounded = Math.round(value * 100) / 100;
  return rounded.toString().replace('.', ',');
}

class KeuzehulpApp extends HTMLElement {
  constructor() {
    super();

    this.quizState = {
      goal: null,
      product: null,
      panelCount: 10,
      moduleCount: null,
    };
    this.totalSteps = 4;

    this.modulesEl = this.querySelector('[data-kh-modules]');
    this.screens = this.querySelectorAll('[data-kh-screen]');

    this.panelSlider = this.querySelector('[data-kh-panel-slider]');
    this.panelNumber = this.querySelector('[data-kh-panel-number]');
    this.previewEl = this.querySelector('[data-kh-preview]');
    this.noticeEl = this.querySelector('[data-kh-notice]');
    this.noticeTextEl = this.querySelector('[data-kh-notice-text]');
    this.noticeCtaEl = this.querySelector('[data-kh-notice-cta]');
    this.noticeContactEl = this.querySelector('[data-kh-notice-contact]');
    this.confirmEl = this.querySelector('[data-kh-confirm]');

    this.renderModules();
    this.updateModules(1);

    this.addEventListener('click', this.onClick.bind(this));
    this.panelSlider.addEventListener('input', () => this.onPanelInput(this.panelSlider.value));
    this.panelNumber.addEventListener('input', () => this.onPanelInput(this.panelNumber.value));
    this.panelNumber.addEventListener('blur', () => {
      this.panelNumber.value = this.quizState.panelCount;
    });
  }

  renderModules() {
    for (let i = 0; i < this.totalSteps; i++) {
      const el = document.createElement('div');
      el.className = 'kh-module';
      this.modulesEl.appendChild(el);
    }
  }

  updateModules(step) {
    this.modulesEl.querySelectorAll('.kh-module').forEach((el, i) => {
      el.classList.toggle('filled', i < step);
    });
  }

  showScreen(n) {
    this.screens.forEach((el) => {
      el.classList.toggle('active', el.dataset.khScreen === String(n));
    });
    this.updateModules(n + 1);
  }

  onClick(event) {
    const target = event.target.closest('[data-kh-action]');
    if (!target || !this.contains(target)) return;

    switch (target.dataset.khAction) {
      case 'next':
        this.goNext(target);
        break;
      case 'back':
        this.goBack(target);
        break;
      case 'switch-to-41':
        this.switchToXds41();
        break;
      case 'result':
        this.showResult();
        break;
      case 'restart':
        this.restart();
        break;
    }
  }

  goNext(target) {
    const fromScreen = parseInt(target.dataset.khFromScreen, 10);

    if (fromScreen === 1) {
      this.quizState.product = target.dataset.heeftOmvormer === 'ja' ? '4.0' : '4.1';
    }

    this.showScreen(fromScreen + 1);

    if (fromScreen === 1) {
      this.updatePreview(this.quizState.panelCount);
    }
  }

  goBack(target) {
    const fromScreen = parseInt(target.dataset.khFromScreen, 10);
    this.showScreen(fromScreen - 1);
  }

  onPanelInput(rawValue) {
    let value = parseInt(rawValue, 10);
    if (Number.isNaN(value)) return;
    value = Math.min(30, Math.max(1, value));
    this.panelSlider.value = value;
    this.panelNumber.value = value;
    this.updatePreview(value);
  }

  // Herberekent de benodigde capaciteit + het modulesadvies voor het huidige
  // aantal panelen en product, en werkt de live preview / meldingen bij.
  updatePreview(panelCount) {
    this.quizState.panelCount = panelCount;

    const capacity = panelCount * PANEL_KWP * PANEL_TO_STORAGE_FACTOR;
    const product = this.quizState.product;
    const modules = getModulesForCapacity(product, capacity);

    const panelLabel = panelCount === 1 ? 'paneel' : 'panelen';
    let previewHtml = `Bij <strong>${panelCount}</strong> ${panelLabel} heb je ongeveer <strong>${formatKwh(capacity)} kWh</strong> aan opslag nodig`;

    if (modules) {
      this.quizState.moduleCount = modules;
      const moduleCapacity = modules * XDS_MAPPING[product].moduleCapacity;
      previewHtml += ` → wij adviseren <strong>${modules} modules</strong> (${formatKwh(moduleCapacity)} kWh)`;

      this.noticeEl.hidden = true;
      this.confirmEl.hidden = false;
    } else {
      this.quizState.moduleCount = null;
      this.confirmEl.hidden = true;
      this.noticeEl.hidden = false;

      if (product === '4.0') {
        this.noticeTextEl.textContent = 'Met dit aantal panelen raden we XDS 4.1 aan voor voldoende capaciteit.';
        this.noticeCtaEl.hidden = false;
        this.noticeContactEl.hidden = true;
      } else {
        this.noticeTextEl.textContent = 'Voor dit aantal panelen adviseren we contact op te nemen voor maatwerk.';
        this.noticeCtaEl.hidden = true;
        this.noticeContactEl.hidden = false;
      }
    }

    this.previewEl.innerHTML = previewHtml;
  }

  // "Zachte redirect" vanuit de XDS 4.0 overflow-melding: zet het product in
  // de quiz-state om naar 4.1 en herberekent direct met de 4.1-staffel.
  switchToXds41() {
    this.quizState.product = '4.1';
    this.updatePreview(this.quizState.panelCount);
  }

  showResult() {
    const product = this.quizState.product;
    const moduleCount = this.quizState.moduleCount;
    if (!moduleCount) return;

    const isXDS41 = product === '4.1';
    const key = XDS_MAPPING[product].specKey;
    const spec = KEUZEHULP_SPECS[key][moduleCount];

    this.querySelector('[data-kh-result-badge]').textContent = isXDS41
      ? 'Aanbevolen — met installateur'
      : 'Aanbevolen — plug & play';
    this.querySelector('[data-kh-result-title]').textContent = isXDS41 ? 'XDS 4.1' : 'XDS 4.0';
    this.querySelector('[data-kh-result-desc]').textContent = isXDS41
      ? 'Sluit rechtstreeks aan op je zonnepanelen voor maximaal rendement. Installatie door een erkend installateur, geregeld via een vrijblijvende offerte.'
      : 'Steek hem zelf in het stopcontact en je bent klaar. Geen installateur, geen gedoe — binnen enkele minuten operationeel.';
    this.querySelector('[data-kh-spec-capacity]').textContent = spec.capacity;
    this.querySelector('[data-kh-spec-modules]').textContent = moduleCount;
    this.querySelector('[data-kh-spec-price]').textContent = spec.price;

    const cta = this.querySelector('[data-kh-result-cta]');
    if (isXDS41) {
      cta.textContent = 'Offerte aanvragen';
      cta.href = '/pages/contact#offerte-formulier';
    } else {
      cta.textContent = 'Bekijk XDS 4.0';
      // Bevestigde handles (Shopify Admin → product → Zoekmachinevermelding):
      //   XDS 4.0: adaptme-plug-and-play-thuisbatterij
      //   XDS 4.1: products-xds-4-1-thuisbatterij-zonnepanelen
      // De aanbevolen capaciteit gaat mee als querystring, zodat de
      // productpagina direct met de juiste "Aantal modules"-pil geselecteerd
      // opent (zie assets/product-variant-from-query.js).
      const capacityParam = encodeURIComponent(spec.capacity);
      cta.href = `/products/adaptme-plug-and-play-thuisbatterij?modules=${capacityParam}`;
    }

    this.showScreen(3);
  }

  restart() {
    this.quizState = { goal: null, product: null, panelCount: 10, moduleCount: null };
    this.panelSlider.value = 10;
    this.panelNumber.value = 10;
    this.noticeEl.hidden = true;
    this.confirmEl.hidden = false;
    this.showScreen(0);
  }
}

customElements.define('keuzehulp-app', KeuzehulpApp);
